import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Globe,
  Inbox,
  Link as LinkIcon,
  Pause,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Rss,
  Search,
  Sparkles,
  Trash2,
  Youtube,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  type ClusterView,
  type DiscoveryItemFilter,
  type DiscoverySourceKindView,
  type DiscoveryStatusView,
  type DiscoveryView,
  type Language,
  type Priority,
  type PromoteDiscoveryInput,
  type RssFeedInputView,
  type RssFeedView,
} from "../../lib/api.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { StatCard } from "../../components/ui/StatCard.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { SchedulerStatusCard } from "./SchedulerStatusCard.js";
import { SourceProposalsCard } from "./SourceProposalsCard.js";

/**
 * Sources — the platform's INPUT layer for content discovery.
 *
 *   - URL Analyzer (top): paste any competitor URL → Firecrawl + Haiku →
 *     structured analysis card + saved discovery record.
 *   - RSS Sources (middle): add / pause / poll feeds. Per-feed status pill +
 *     last-fetched timestamp + actions menu.
 *   - Discovery Inbox (bottom): unified feed of "things to consider writing
 *     about" with triage chips (new / triaged / promoted / ignored).
 *
 * Triage actions (per item):
 *   - Promote → opens a small dialog to create a keyword from this discovery
 *   - Ignore   → marks as "ignored"
 *   - Triaged  → marks as "triaged" (operator has reviewed, no action yet)
 */

export function SourcesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<DiscoveryStatusView | "all">("new");
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState<DiscoveryView | null>(null);

  const filter: DiscoveryItemFilter = {
    status: statusFilter === "all" ? undefined : statusFilter,
    q: search || undefined,
  };

  const sources = useQuery({ queryKey: ["discovery", "sources"], queryFn: () => api.listDiscoverySources() });
  const items = useQuery({
    queryKey: ["discovery", "items", filter],
    queryFn: () => api.listDiscoveryItems(filter),
    refetchInterval: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["discovery"] });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-ink">Sources</h2>
        <p className="mt-1 text-sm text-muted">
          Where new content comes from. Paste a URL to analyse a single competitor article, or wire up RSS feeds for ongoing discovery.
        </p>
      </div>

      {/* KPI row */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Active sources"
          value={
            sources.isPending
              ? "—"
              : (sources.data?.sources.filter((s) => s.active).length ?? 0)
          }
          icon={Rss}
          tone="brand"
          hint={sources.data ? `${sources.data.count} total feeds` : undefined}
        />
        <StatCard
          label="New discoveries"
          value={items.isPending ? "—" : items.data?.countsByStatus.new ?? 0}
          icon={Inbox}
          tone="warn"
          hint="awaiting triage"
        />
        <StatCard
          label="Triaged"
          value={items.isPending ? "—" : items.data?.countsByStatus.triaged ?? 0}
          icon={CheckCircle2}
          tone="accent"
          hint="reviewed, no action yet"
        />
        <StatCard
          label="Promoted to keywords"
          value={items.isPending ? "—" : items.data?.countsByStatus.promoted ?? 0}
          icon={Sparkles}
          tone="brand"
          hint="turned into pipeline work"
        />
      </section>

      {/* Competitor footprints → topic proposals (approve to create keywords) */}
      <SourceProposalsCard />

      {/* Scheduler status — auto-poll lifecycle + recent sweeps */}
      <SchedulerStatusCard />

      {/* URL Analyzer */}
      <UrlAnalyzerCard onComplete={(d) => {
        invalidate();
        setSelectedItem(d);
      }} />

      {/* RSS Sources */}
      <SourcesTable
        sources={sources.data?.sources ?? []}
        loading={sources.isPending}
        onChanged={invalidate}
      />

      {/* Discovery inbox */}
      <SectionCard
        title="Discovery inbox"
        subtitle={`${items.data?.count ?? 0} item${items.data?.count === 1 ? "" : "s"} match the current filters`}
        icon={Inbox}
        padded={false}
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search url, title, publisher…"
                className="input w-56 py-1.5 pl-8 text-xs"
              />
            </div>
          </div>
        }
      >
        <div className="border-b border-line px-5 py-3">
          <div className="flex flex-wrap gap-1.5">
            <StatusChip current={statusFilter} value="all" label="All" onClick={setStatusFilter} count={items.data?.count} />
            <StatusChip current={statusFilter} value="new" label="New" onClick={setStatusFilter} count={items.data?.countsByStatus.new} />
            <StatusChip current={statusFilter} value="triaged" label="Triaged" onClick={setStatusFilter} count={items.data?.countsByStatus.triaged} />
            <StatusChip current={statusFilter} value="promoted" label="Promoted" onClick={setStatusFilter} count={items.data?.countsByStatus.promoted} />
            <StatusChip current={statusFilter} value="ignored" label="Ignored" onClick={setStatusFilter} count={items.data?.countsByStatus.ignored} />
          </div>
        </div>
        {items.isPending ? (
          <DiscoverySkeleton />
        ) : items.data && items.data.items.length > 0 ? (
          <ul className="divide-y divide-line">
            {items.data.items.map((d) => (
              <DiscoveryRow key={d.id} d={d} onSelect={() => setSelectedItem(d)} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={Radar}
            title="No discoveries match these filters"
            description="Paste a URL above or add an RSS feed to start populating the inbox."
          />
        )}
      </SectionCard>

      {selectedItem && (
        <DiscoveryDetailDrawer
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// URL Analyzer card
// ───────────────────────────────────────────────────────────────────────────

function UrlAnalyzerCard({ onComplete }: { onComplete: (d: DiscoveryView) => void }) {
  const [url, setUrl] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const trimmed = url.trim();
  const isYoutube = /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch|shorts|embed|live|v)|youtu\.be\/)/.test(trimmed);

  const analyze = useMutation({
    mutationFn: () => isYoutube ? api.analyzeYoutube(trimmed) : api.analyzeUrl(trimmed),
    onSuccess: (r) => {
      setUrl("");
      setWarnings(r.warnings);
      setError(null);
      onComplete(r.discovery);
    },
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmed || analyze.isPending) return;
    try { new URL(trimmed); } catch { setError("Not a valid URL"); return; }
    setError(null);
    analyze.mutate();
  }

  return (
    <SectionCard
      title="Analyze a URL"
      subtitle="Articles → structural + gap analysis. YouTube → transcript + suggested outline."
      icon={LinkIcon}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://competitor.co.il/article  —  or  —  https://youtube.com/watch?v=…"
            className="input flex-1 font-mono text-xs"
            disabled={analyze.isPending}
          />
          <button type="submit" className="btn-primary" disabled={!trimmed || analyze.isPending}>
            {analyze.isPending ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Analyzing…
              </>
            ) : (
              <>
                {isYoutube ? <YoutubeMark /> : <Radar className="h-3.5 w-3.5" />}
                Analyze
              </>
            )}
          </button>
        </div>
        {isYoutube && trimmed && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-xs ring-1 ring-inset ring-red-100">
            <span className="inline-flex items-center gap-1.5 font-semibold text-red-700">
              <YoutubeMark />
              YouTube detected
            </span>
            <span className="ml-1.5 text-red-800/80">
              — will fetch title + description + stats + transcript, then derive purpose, hook, key takeaways, and a suggested H2 outline you can promote into a new article keyword.
            </span>
          </div>
        )}
        {!isYoutube && (
          <p className="text-xs text-muted">
            Standard URL → Firecrawl extracts H1/H2/H3 + Claude Haiku for entities + cluster match + gap analysis. ~5–10s · ~$0.001 in Claude tokens.
          </p>
        )}
        {isYoutube && (
          <p className="text-xs text-muted">
            YouTube URL → youtubei.js for metadata + captions, then Claude Haiku derives purpose, hook, audience, key takeaways, and a suggested H2 outline. ~15–30s · ~$0.002–0.005 in Claude tokens.
          </p>
        )}
        {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700">{error}</div>}
        {warnings.length > 0 && (
          <div className="space-y-1">
            {warnings.map((w, i) => (
              <div key={i} className="rounded-lg bg-warn-soft px-3 py-1.5 text-xs text-amber-800">
                ⚠ {w}
              </div>
            ))}
          </div>
        )}
      </form>
    </SectionCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sources table (RSS feeds)
// ───────────────────────────────────────────────────────────────────────────

function SourcesTable({
  sources,
  loading,
  onChanged,
}: {
  sources: RssFeedView[];
  loading: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);

  const pollAll = useMutation({
    mutationFn: () => api.pollAllDiscoverySources({ analyze: false }),
    onSuccess: () => onChanged(),
  });

  return (
    <SectionCard
      title="RSS feeds"
      subtitle="Polled on demand or by an external scheduler. Tighter feeds = faster discovery."
      icon={Rss}
      padded={false}
      action={
        <div className="flex items-center gap-2">
          <button
            onClick={() => pollAll.mutate()}
            disabled={pollAll.isPending || sources.length === 0}
            className="btn-secondary btn-sm"
            title="Poll every due feed now"
          >
            {pollAll.isPending ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Polling…
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                Poll all
              </>
            )}
          </button>
          <button onClick={() => setAdding(true)} className="btn-primary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add feed
          </button>
        </div>
      }
    >
      {loading ? (
        <SourcesSkeleton />
      ) : sources.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-canvas/40 text-2xs font-semibold uppercase tracking-wider text-muted">
              <th className="px-5 py-2.5 text-left font-semibold">Feed</th>
              <th className="px-3 py-2.5 text-left font-semibold">Cluster</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-3 py-2.5 text-left font-semibold">Interval</th>
              <th className="px-3 py-2.5 text-left font-semibold">Last fetched</th>
              <th className="px-5 py-2.5 text-right" />
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <SourceRow key={s.id} source={s} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          icon={Rss}
          title="No feeds configured yet"
          description="Add a feed above to start automated discovery from external sources."
        />
      )}

      {pollAll.data && (
        <div className="border-t border-line bg-canvas-deep/40 px-5 py-3 text-xs text-muted">
          ✓ Polled {pollAll.data.count} feed{pollAll.data.count === 1 ? "" : "s"} ·
          {" "}{pollAll.data.totals.itemsNew} new of {pollAll.data.totals.itemsSeen} seen
          {pollAll.data.totals.itemsAnalyzed > 0 && ` · ${pollAll.data.totals.itemsAnalyzed} analyzed`}
        </div>
      )}

      {adding && (
        <AddFeedDialog
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            onChanged();
          }}
        />
      )}
    </SectionCard>
  );
}

function SourceRow({ source, onChanged }: { source: RssFeedView; onChanged: () => void }) {
  const poll = useMutation({
    mutationFn: (analyze: boolean) => api.pollDiscoverySource(source.id, { analyze }),
    onSuccess: () => onChanged(),
  });
  const toggle = useMutation({
    mutationFn: () => api.patchDiscoverySource(source.id, { active: !source.active }),
    onSuccess: () => onChanged(),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteDiscoverySource(source.id),
    onSuccess: () => onChanged(),
  });

  return (
    <tr className="border-b border-line transition last:border-b-0 hover:bg-canvas/40">
      <td className="px-5 py-3 align-top">
        <div className="flex items-center gap-2.5">
          {source.active ? (
            <Circle className="h-2 w-2 fill-emerald-500 stroke-none animate-pulse-soft" />
          ) : (
            <Circle className="h-2 w-2 fill-line-dark stroke-none" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-medium text-ink">{source.name}</span>
              <SourceKindBadge kind={source.kind} />
            </div>
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="block truncate text-2xs text-brand-700 hover:underline"
            >
              {source.url}
            </a>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 align-top text-xs text-muted">{source.defaultClusterId ?? "—"}</td>
      <td className="px-3 py-3 align-top">
        {source.lastFetchSucceeded ? (
          <span className="pill-ok">
            <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
            Healthy
          </span>
        ) : (
          <span className="pill-danger" title={source.lastFetchError ?? undefined}>
            <AlertCircle className="h-3 w-3" />
            Failing
          </span>
        )}
      </td>
      <td className="px-3 py-3 align-top text-xs text-muted">{source.pollIntervalMinutes}m</td>
      <td className="px-3 py-3 align-top text-xs text-muted">
        {source.lastFetchedAt ? relativeTime(source.lastFetchedAt) : "never"}
        {source.lastItemCount > 0 && (
          <span className="ml-2 text-2xs text-muted/70">· {source.lastItemCount} items</span>
        )}
      </td>
      <td className="px-5 py-3 align-top">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => poll.mutate(false)}
            disabled={poll.isPending}
            className="btn-ghost btn-sm"
            title="Poll this feed now"
          >
            {poll.isPending ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
            className="btn-ghost btn-sm"
            title={source.active ? "Pause polling" : "Resume polling"}
          >
            {source.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={() => {
              if (confirm(`Delete RSS feed "${source.name}"? (Existing discoveries stay.)`)) {
                remove.mutate();
              }
            }}
            disabled={remove.isPending}
            className="btn-ghost btn-sm text-danger"
            title="Delete feed"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function SourceKindBadge({ kind }: { kind: DiscoverySourceKindView }) {
  if (kind === "youtube_channel") {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-danger-soft px-1.5 py-0.5 text-2xs font-medium text-red-700">
        <Youtube className="h-2.5 w-2.5" strokeWidth={2.4} />
        YouTube
      </span>
    );
  }
  if (kind === "sitemap") {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-warn-soft px-1.5 py-0.5 text-2xs font-medium text-amber-700">
        <Radar className="h-2.5 w-2.5" strokeWidth={2.4} />
        Sitemap
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-info-soft px-1.5 py-0.5 text-2xs font-medium text-blue-700">
      <Rss className="h-2.5 w-2.5" strokeWidth={2.4} />
      RSS
    </span>
  );
}

function SourcesSkeleton() {
  return (
    <ul className="divide-y divide-line">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="flex items-center justify-between gap-3 px-5 py-3">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-2 w-2/3" />
          </div>
          <Skeleton className="h-3 w-20" />
        </li>
      ))}
    </ul>
  );
}

function AddFeedDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [draft, setDraft] = useState<RssFeedInputView>({
    id: "",
    name: "",
    url: "",
    kind: "rss",
    language: "he",
    pollIntervalMinutes: 60,
    autoAnalyze: false,
    active: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [channelInput, setChannelInput] = useState("");
  const kind: DiscoverySourceKindView = draft.kind ?? "rss";

  const add = useMutation({
    mutationFn: () => api.addDiscoverySource(draft),
    onSuccess: () => onAdded(),
    onError: (e: Error) => setError(e.message),
  });
  const watch = useMutation({
    mutationFn: () => api.watchYoutubeChannel({ input: channelInput.trim(), name: draft.name || null, language: draft.language, autoAnalyze: draft.autoAnalyze }),
    onSuccess: () => onAdded(),
    onError: (e: Error) => setError(e.message),
  });
  const busy = add.isPending || watch.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (kind === "youtube_channel") {
      if (!channelInput.trim()) {
        setError("Enter a channel handle (@name), URL, or UC… id.");
        return;
      }
      watch.mutate();
      return;
    }
    if (!draft.id || !draft.name || !draft.url) {
      setError("All three of id, name, url are required.");
      return;
    }
    add.mutate();
  }

  return (
    <div className="border-t border-line bg-canvas-deep/40 px-5 py-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm font-semibold text-ink">Add a discovery source</div>
            <div className="text-xs text-muted">RSS feed, site sitemap, or a YouTube channel to watch — all feed the same discovery inbox.</div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Kind toggle */}
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setDraft({ ...draft, kind: "rss" })}
            className={kind === "rss"
              ? "rounded-lg border-2 border-brand bg-brand-50 px-3 py-2 text-left"
              : "rounded-lg border border-line bg-white px-3 py-2 text-left hover:border-muted"}
          >
            <div className="flex items-center gap-2">
              <Rss className="h-4 w-4 text-brand-700" />
              <span className="text-sm font-semibold text-ink">RSS feed</span>
            </div>
            <div className="mt-0.5 text-2xs text-muted">Standard RSS / Atom URL</div>
          </button>
          <button
            type="button"
            onClick={() => setDraft({ ...draft, kind: "sitemap" })}
            className={kind === "sitemap"
              ? "rounded-lg border-2 border-brand bg-brand-50 px-3 py-2 text-left"
              : "rounded-lg border border-line bg-white px-3 py-2 text-left hover:border-muted"}
          >
            <div className="flex items-center gap-2">
              <Radar className="h-4 w-4 text-brand-700" />
              <span className="text-sm font-semibold text-ink">Site sitemap</span>
            </div>
            <div className="mt-0.5 text-2xs text-muted">/sitemap.xml or site root</div>
          </button>
          <button
            type="button"
            onClick={() => setDraft({ ...draft, kind: "youtube_channel", autoAnalyze: true })}
            className={kind === "youtube_channel"
              ? "rounded-lg border-2 border-brand bg-brand-50 px-3 py-2 text-left"
              : "rounded-lg border border-line bg-white px-3 py-2 text-left hover:border-muted"}
          >
            <div className="flex items-center gap-2">
              <Youtube className="h-4 w-4 text-brand-700" />
              <span className="text-sm font-semibold text-ink">YouTube channel</span>
            </div>
            <div className="mt-0.5 text-2xs text-muted">Watch new uploads</div>
          </button>
        </div>

        {kind === "youtube_channel" ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="label">Channel handle, URL, or id <span className="text-danger">*</span></label>
              <input
                type="text"
                value={channelInput}
                onChange={(e) => setChannelInput(e.target.value)}
                placeholder="@tasmc1  ·  youtube.com/@tasmc1  ·  UCxxxx…"
                className="input font-mono text-xs"
              />
              <p className="mt-1 text-2xs text-muted">We resolve the channel id automatically and watch its free uploads feed. The latest few uploads are pulled in on add (so you see results right away); after that, only new uploads. Each gets the YouTube scope-fit analysis. No API key needed.</p>
            </div>
            <div>
              <label className="label">Display name (optional)</label>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="defaults to the channel's name"
                className="input"
              />
            </div>
            <div>
              <label className="label">Produce content in</label>
              <select
                value={draft.language}
                onChange={(e) => setDraft({ ...draft, language: e.target.value as "he" | "en" | "other" })}
                className="input"
              >
                <option value="he">Hebrew</option>
                <option value="en">English</option>
                <option value="other">Match the video</option>
              </select>
              <p className="mt-1 text-2xs text-muted">Watch an English channel → get Hebrew keywords + outline for the Israeli audience. "Match the video" keeps the source language.</p>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="label">Id <span className="text-danger">*</span></label>
            <input
              type="text"
              value={draft.id}
              onChange={(e) => setDraft({ ...draft, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-") })}
              placeholder="alternative-medicine-co-il"
              className="input font-mono text-xs"
            />
          </div>
          <div>
            <label className="label">Display name <span className="text-danger">*</span></label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Alternative Medicine"
              className="input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">
              {kind === "rss" ? "Feed URL" : "Sitemap or site URL"} <span className="text-danger">*</span>
            </label>
            <input
              type="url"
              value={draft.url}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              placeholder={
                kind === "rss"
                  ? "https://alternativemedicine.co.il/feed/"
                  : "https://alternativemedicine.co.il/sitemap.xml"
              }
              className="input font-mono text-xs"
            />
            {kind === "sitemap" && (
              <p className="mt-1 text-2xs text-muted">
                Site root (e.g. <code className="font-mono">https://example.com/</code>) also works — we try <code className="font-mono">/sitemap.xml</code> automatically.
              </p>
            )}
          </div>
          <div>
            <label className="label">Language</label>
            <select
              value={draft.language}
              onChange={(e) => setDraft({ ...draft, language: e.target.value as "he" | "en" | "other" })}
              className="input"
            >
              <option value="he">Hebrew</option>
              <option value="en">English</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="label">Poll interval (minutes)</label>
            <input
              type="number"
              min={5}
              value={draft.pollIntervalMinutes ?? 60}
              onChange={(e) => setDraft({ ...draft, pollIntervalMinutes: parseInt(e.target.value, 10) || 60 })}
              className="input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.autoAnalyze ?? false}
                onChange={(e) => setDraft({ ...draft, autoAnalyze: e.target.checked })}
              />
              <span>Auto-analyze every new item (slower + costs LLM tokens; default off — operator triggers per item)</span>
            </label>
          </div>
        </div>
        )}
        {kind === "youtube_channel" && (
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={draft.autoAnalyze ?? true}
              onChange={(e) => setDraft({ ...draft, autoAnalyze: e.target.checked })}
            />
            <span>Auto-analyze new uploads (transcript + scope-fit, ~$0.001/video). On by default for channels.</span>
          </label>
        )}
        {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700">{error}</div>}
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary btn-sm">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary btn-sm">
            {busy ? (kind === "youtube_channel" ? "Resolving…" : "Adding…") : (kind === "youtube_channel" ? "Watch channel" : "Add feed")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Discovery rows + detail drawer
// ───────────────────────────────────────────────────────────────────────────

function StatusChip({
  current,
  value,
  label,
  count,
  onClick,
}: {
  current: DiscoveryStatusView | "all";
  value: DiscoveryStatusView | "all";
  label: string;
  count?: number;
  onClick: (v: DiscoveryStatusView | "all") => void;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      className={
        active
          ? "inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-2.5 py-1 text-2xs font-medium text-white"
          : "inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-2xs font-medium text-muted-dark ring-1 ring-line hover:bg-canvas hover:text-ink"
      }
    >
      {label}
      {typeof count === "number" && (
        <span className={
          active
            ? "rounded-full bg-white/20 px-1.5 text-2xs font-semibold"
            : "rounded-full bg-line-soft px-1.5 text-2xs font-semibold"
        }>{count}</span>
      )}
    </button>
  );
}

function DiscoveryRow({ d, onSelect }: { d: DiscoveryView; onSelect: () => void }) {
  return (
    <li
      onClick={onSelect}
      className="cursor-pointer px-5 py-3.5 transition hover:bg-canvas/60"
    >
      <div className="flex items-start gap-3">
        <KindIcon kind={d.kind} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-ink" dir={d.language === "he" ? "rtl" : "ltr"}>
              {d.title ?? d.url}
            </span>
            <StatusPill status={d.status} />
          </div>
          {d.analysis.recommendation && (
            <div className="mt-1 text-xs text-ink">
              <span className="font-medium text-brand-700">↳ </span>
              {d.analysis.recommendation}
            </div>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted">
            {d.publisher && <span className="truncate">{d.publisher}</span>}
            <span className="truncate font-mono text-2xs">{shortenUrl(d.url)}</span>
            <span>{relativeTime(d.discoveredAt)}</span>
            {d.analysis.entities.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Globe className="h-3 w-3" />
                {d.analysis.entities.slice(0, 4).join(", ")}{d.analysis.entities.length > 4 && "…"}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function KindIcon({ kind }: { kind: DiscoveryView["kind"] }) {
  if (kind === "youtube_video") {
    return (
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-700">
        <YoutubeMark />
      </div>
    );
  }
  const map: Record<Exclude<DiscoveryView["kind"], "youtube_video">, { icon: LucideIcon; tint: string }> = {
    url_analysis: { icon: LinkIcon, tint: "bg-brand-50 text-brand-700" },
    rss_item: { icon: Rss, tint: "bg-info-soft text-blue-700" },
    crawl_find: { icon: Radar, tint: "bg-warn-soft text-amber-700" },
  };
  const meta = map[kind];
  const Icon = meta.icon;
  return (
    <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.tint}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
    </div>
  );
}

/**
 * Inline YouTube glyph (single SVG, no extra dep). Lucide doesn't ship a
 * YouTube-specific icon under a non-marks license, so we hand-roll one.
 */
function YoutubeMark({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M21.6 7.18a2.5 2.5 0 0 0-1.76-1.77C18.27 5 12 5 12 5s-6.27 0-7.84.41A2.5 2.5 0 0 0 2.4 7.18 26.14 26.14 0 0 0 2 12a26.14 26.14 0 0 0 .4 4.82 2.5 2.5 0 0 0 1.76 1.77C5.73 19 12 19 12 19s6.27 0 7.84-.41a2.5 2.5 0 0 0 1.76-1.77A26.14 26.14 0 0 0 22 12a26.14 26.14 0 0 0-.4-4.82ZM10 15V9l5.2 3Z" />
    </svg>
  );
}

/**
 * YouTube-specific drawer panel — surfaces the rich metadata + transcript-driven
 * outline that the YouTube analyzer derives. The "Suggested H2 outline" block
 * is what a future "transcribe-to-article" production mode will seed the brief
 * with when the operator promotes this discovery into a keyword.
 */
function YoutubePanel({ yt, videoUrl }: { yt: import("../../lib/api.js").YoutubeExtrasView; videoUrl: string }) {
  const dirAttr = (s: string | null | undefined) => /[֐-׿]/.test(s ?? "") ? "rtl" : "ltr";
  const minutes = yt.durationSeconds !== null ? Math.floor(yt.durationSeconds / 60) : null;
  const seconds = yt.durationSeconds !== null ? yt.durationSeconds % 60 : null;
  return (
    <div className="space-y-4 rounded-xl border border-red-200/60 bg-red-50/30 p-4">
      <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-red-700">
        <YoutubeMark />
        YouTube video
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_1fr]">
        {yt.thumbnailUrl ? (
          <a href={videoUrl} target="_blank" rel="noreferrer" className="block w-full max-w-[200px] overflow-hidden rounded-lg ring-1 ring-line">
            <img src={yt.thumbnailUrl} alt="" className="block w-full" />
          </a>
        ) : null}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-2xs text-muted">
          {yt.channelName && (
            <div className="col-span-2">
              <dt className="font-semibold uppercase tracking-wider">Channel</dt>
              <dd className="mt-0.5 text-sm text-ink">{yt.channelName}</dd>
            </div>
          )}
          {minutes !== null && (
            <div>
              <dt className="font-semibold uppercase tracking-wider">Duration</dt>
              <dd className="mt-0.5 text-sm text-ink">{minutes}:{String(seconds).padStart(2, "0")}</dd>
            </div>
          )}
          {yt.viewCount !== null && (
            <div>
              <dt className="font-semibold uppercase tracking-wider">Views</dt>
              <dd className="mt-0.5 text-sm text-ink">{yt.viewCount.toLocaleString()}</dd>
            </div>
          )}
          {yt.likeCount !== null && (
            <div>
              <dt className="font-semibold uppercase tracking-wider">Likes</dt>
              <dd className="mt-0.5 text-sm text-ink">{yt.likeCount.toLocaleString()}</dd>
            </div>
          )}
          {yt.transcriptWordCount !== null && (
            <div>
              <dt className="font-semibold uppercase tracking-wider">Transcript</dt>
              <dd className="mt-0.5 text-sm text-ink">
                {yt.transcriptWordCount.toLocaleString()} words {yt.transcriptLanguage && `· ${yt.transcriptLanguage}`}
              </dd>
            </div>
          )}
          {yt.audience && (
            <div className="col-span-2">
              <dt className="font-semibold uppercase tracking-wider">Audience</dt>
              <dd className="mt-0.5 text-sm text-ink">{yt.audience}</dd>
            </div>
          )}
        </dl>
      </div>

      {yt.purpose && (
        <section>
          <h5 className="mb-1 text-2xs font-semibold uppercase tracking-wider text-muted">What it's really about</h5>
          <p className="text-sm text-ink" dir={dirAttr(yt.purpose)}>{yt.purpose}</p>
        </section>
      )}

      {yt.hook && (
        <section>
          <h5 className="mb-1 text-2xs font-semibold uppercase tracking-wider text-muted">Hook</h5>
          <p className="text-sm font-medium text-ink" dir={dirAttr(yt.hook)}>"{yt.hook}"</p>
        </section>
      )}

      {yt.keyTakeaways.length > 0 && (
        <section>
          <h5 className="mb-1 text-2xs font-semibold uppercase tracking-wider text-muted">Key takeaways</h5>
          <ul className="ml-4 list-decimal space-y-1 text-sm text-ink" dir={dirAttr(yt.keyTakeaways[0])}>
            {yt.keyTakeaways.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </section>
      )}

      {yt.suggestedOutline.length > 0 && (
        <section>
          <h5 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">Suggested article outline</h5>
          <ol className="space-y-2">
            {yt.suggestedOutline.map((s, i) => (
              <li key={i} className="rounded-lg bg-white p-2.5 ring-1 ring-line">
                <div className="text-sm font-medium text-ink" dir={dirAttr(s.h2)}>
                  <span className="text-muted">H2 {i + 1}.</span> {s.h2}
                </div>
                {s.summary && (
                  <div className="mt-0.5 text-xs text-muted" dir={dirAttr(s.summary)}>{s.summary}</div>
                )}
              </li>
            ))}
          </ol>
          <p className="mt-2 text-2xs text-muted/80">
            Use "Promote to keyword" below to carry this outline forward — the new keyword will be seeded with these H2s + the transcript as brief context.
          </p>
        </section>
      )}

      {yt.transcriptExcerpt && (
        <details className="rounded-lg bg-white p-3 ring-1 ring-line">
          <summary className="cursor-pointer text-2xs font-semibold uppercase tracking-wider text-muted">Transcript excerpt ({yt.transcriptWordCount} words total)</summary>
          <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-muted-dark" dir={dirAttr(yt.transcriptExcerpt)}>
            {yt.transcriptExcerpt}
          </p>
        </details>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: DiscoveryStatusView }) {
  const map: Record<DiscoveryStatusView, string> = {
    new: "pill-warn",
    triaged: "pill-info",
    promoted: "pill-ok",
    ignored: "pill-muted",
    stale: "pill-muted",
  };
  return <span className={map[status]}>{status}</span>;
}

function DiscoverySkeleton() {
  return (
    <ul className="divide-y divide-line">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-5 py-3.5">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-2 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function DiscoveryDetailDrawer({
  item,
  onClose,
  onChanged,
}: {
  item: DiscoveryView;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [promoting, setPromoting] = useState<false | { override: boolean }>(false);

  const triage = useMutation({
    mutationFn: (status: DiscoveryStatusView) => api.patchDiscoveryItem(item.id, { status }),
    onSuccess: () => onChanged(),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteDiscoveryItem(item.id),
    onSuccess: () => { onChanged(); onClose(); },
  });

  // Detect whether Claude recommended skipping this discovery — when true we
  // render the Recommendation banner in a more muted "AI-says-no" style and
  // surface an explicit override affordance so the operator knows they're
  // making the judgment call.
  const skipRecommended = isSkipRecommendation(item.analysis.recommendation);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[1px] animate-fade-in" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-rail animate-slide-in-right">
        <header className="flex items-start justify-between gap-3 border-b border-line p-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <StatusPill status={item.status} />
              <span className="text-2xs uppercase tracking-wider text-muted">{item.kind.replace("_", " ")}</span>
            </div>
            <h3 className="mt-1.5 truncate text-base font-semibold text-ink" dir={item.language === "he" ? "rtl" : "ltr"}>
              {item.title ?? item.url}
            </h3>
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="mt-0.5 block truncate text-xs text-brand-700 hover:underline"
            >
              {item.url}
            </a>
          </div>
          <button onClick={onClose} className="btn-icon" title="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
          {item.analysis.recommendation && !skipRecommended && (
            <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
              <div className="text-2xs font-semibold uppercase tracking-wider text-brand-700">Recommendation</div>
              <div className="mt-1.5 text-sm text-ink">{item.analysis.recommendation}</div>
            </div>
          )}

          {item.analysis.recommendation && skipRecommended && item.status !== "promoted" && (
            <div className="rounded-xl border border-warn/30 bg-warn-soft p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-warn/15 text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-amber-700">
                    AI suggests skipping
                  </div>
                  <div className="mt-1 text-sm text-ink">{item.analysis.recommendation}</div>
                  <div className="mt-2 text-2xs text-amber-800/80">
                    Operator override available — if you see an angle the AI missed, promote anyway.
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setPromoting({ override: true })}
                      disabled={triage.isPending}
                      className="btn-primary btn-sm gap-1.5"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Add anyway → promote to keyword
                    </button>
                    {item.status !== "ignored" && (
                      <button
                        onClick={() => triage.mutate("ignored")}
                        disabled={triage.isPending}
                        className="btn-secondary btn-sm"
                      >
                        Or ignore
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Already-promoted: show the recommendation in a muted style, no override button. */}
          {item.analysis.recommendation && skipRecommended && item.status === "promoted" && (
            <div className="rounded-xl border border-line bg-canvas-deep/40 p-4">
              <div className="text-2xs font-semibold uppercase tracking-wider text-muted">
                AI suggested skipping (overridden — promoted)
              </div>
              <div className="mt-1.5 text-sm text-muted">{item.analysis.recommendation}</div>
            </div>
          )}

          {item.analysis.gapAnalysis && (
            <section>
              <h4 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">Gap analysis</h4>
              <p className="text-sm text-ink">{item.analysis.gapAnalysis}</p>
            </section>
          )}

          {item.analysis.youtube && <YoutubePanel yt={item.analysis.youtube} videoUrl={item.url} />}

          {(item.analysis.entities.length > 0 || item.analysis.inferredKeyword) && (
            <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {item.analysis.inferredKeyword && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Inferred keyword</div>
                  <div className="mt-1 text-sm" dir={item.language === "he" ? "rtl" : "ltr"}>{item.analysis.inferredKeyword}</div>
                </div>
              )}
              {item.analysis.inferredClusterId && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Inferred cluster</div>
                  <div className="mt-1 text-sm">{item.analysis.inferredClusterId}</div>
                </div>
              )}
              {item.analysis.matchedKeywordId && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Matches your keyword</div>
                  <div className="mt-1 font-mono text-xs">{item.analysis.matchedKeywordId}</div>
                </div>
              )}
              {item.analysis.wordCount !== null && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Length</div>
                  <div className="mt-1 text-sm">{item.analysis.wordCount.toLocaleString()} words · {item.analysis.h2s.length} H2s</div>
                </div>
              )}
            </section>
          )}

          {item.analysis.entities.length > 0 && (
            <section>
              <h4 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">Entities covered</h4>
              <div className="flex flex-wrap gap-1.5">
                {item.analysis.entities.map((e, i) => (
                  <span key={i} className="rounded-full bg-line-soft px-2 py-0.5 text-2xs text-muted-dark">{e}</span>
                ))}
              </div>
            </section>
          )}

          {item.analysis.h2s.length > 0 && (
            <section>
              <h4 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">Structure</h4>
              {item.analysis.h1 && (
                <div className="text-sm font-semibold text-ink" dir={item.language === "he" ? "rtl" : "ltr"}>
                  H1: {item.analysis.h1}
                </div>
              )}
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-muted" dir={item.language === "he" ? "rtl" : "ltr"}>
                {item.analysis.h2s.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </section>
          )}

          {item.excerpt && (
            <section>
              <h4 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">Excerpt</h4>
              <p className="text-xs text-muted" dir={item.language === "he" ? "rtl" : "ltr"}>{item.excerpt}</p>
            </section>
          )}

          {!item.analysis.analyzedOk && (
            <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700">
              Analysis failed or was incomplete — recommendation may be unreliable.
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-line bg-canvas-deep/40 px-5 py-3">
          <div className="text-2xs text-muted">
            Discovered {relativeTime(item.discoveredAt)}
            {item.analysis.llmModel && ` · ${item.analysis.llmModel}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="btn-ghost btn-sm text-danger"
              title="Delete this discovery"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            {item.status !== "ignored" && (
              <button onClick={() => triage.mutate("ignored")} disabled={triage.isPending} className="btn-secondary btn-sm">
                Ignore
              </button>
            )}
            {item.status !== "triaged" && item.status !== "promoted" && (
              <button onClick={() => triage.mutate("triaged")} disabled={triage.isPending} className="btn-secondary btn-sm">
                Mark triaged
              </button>
            )}
            {item.status !== "promoted" && (
              <button
                onClick={() => setPromoting({ override: skipRecommended })}
                disabled={triage.isPending}
                className="btn-primary btn-sm"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {skipRecommended ? "Promote anyway" : "Promote to keyword"}
              </button>
            )}
          </div>
        </footer>
      </aside>

      {promoting && (
        <PromoteToKeywordDialog
          item={item}
          override={promoting.override}
          onCancel={() => setPromoting(false)}
          onPromoted={() => {
            setPromoting(false);
            onChanged();
            onClose();
          }}
        />
      )}
    </>
  );
}

function PromoteToKeywordDialog({
  item,
  override,
  onCancel,
  onPromoted,
}: {
  item: DiscoveryView;
  /** True when the operator opened this from a 'Skip' recommendation — drops a heads-up at the top so they know the override is intentional. */
  override?: boolean;
  onCancel: () => void;
  onPromoted: (keywordId: string) => void;
}) {
  // Default fields from the analyzer.
  const defaultLanguage: Language = item.language === "en" ? "en" : "he";
  const [draft, setDraft] = useState<PromoteDiscoveryInput>({
    keyword: item.analysis.inferredKeyword ?? item.title ?? "",
    language: defaultLanguage,
    clusterId: item.analysis.inferredClusterId,
    priority: "medium",
    pillarPage: false,
    intent: "informational",
  });
  const [error, setError] = useState<string | null>(null);

  // Cluster picker — fetch the existing clusters so the operator can override.
  const clustersQuery = useQuery({
    queryKey: ["pipeline", "clusters"],
    queryFn: () => api.listClusters(),
  });

  const promote = useMutation({
    mutationFn: () => api.promoteDiscovery(item.id, { ...draft, override: override === true }),
    onSuccess: (r) => onPromoted(r.keyword.id),
    onError: (e: Error) => setError(e.message),
  });

  // Suggest the main keyword from the title (LLM candidates + DataForSEO volumes).
  const suggest = useMutation({
    mutationFn: () =>
      api.suggestDiscoveryKeyword({ title: item.title ?? draft.keyword, language: draft.language }),
    onSuccess: (r) => { if (r.suggested) setDraft((d) => ({ ...d, keyword: r.suggested! })); },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!draft.keyword.trim()) {
      setError("Keyword text is required.");
      return;
    }
    promote.mutate();
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-sm animate-fade-in" onClick={onCancel} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <form
          onSubmit={handleSubmit}
          className="pointer-events-auto w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-rail animate-fade-in"
        >
          <header>
            <div className="flex items-center gap-2 text-2xs uppercase tracking-wider text-brand-700">
              <Sparkles className="h-3.5 w-3.5" />
              {override ? "Operator override" : "Promote to keyword"}
            </div>
            <h3 className="mt-1 text-lg font-semibold text-ink">
              {override ? "Add anyway → new pipeline entry" : "Create a new pipeline entry"}
            </h3>
            <p className="mt-1 text-xs text-muted text-balance">
              The discovery's recommendation + gap analysis will carry forward as the keyword's notes.
              You can edit the brief afterwards in the Pipeline page.
            </p>
          </header>

          {override && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" strokeWidth={2.4} />
                <div className="min-w-0">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-amber-700">
                    AlternaBe AI flagged this as out of scope
                  </div>
                  {item.analysis.recommendation && (
                    <p className="mt-1 text-xs text-amber-900">
                      <span className="font-medium">It said:</span> {item.analysis.recommendation}
                    </p>
                  )}
                  <p className="mt-1.5 text-2xs text-amber-800/80">
                    You're overriding the recommendation. Make sure you have a clear editorial angle before producing —
                    this keyword will be tagged <code className="rounded bg-white/60 px-1 font-mono text-2xs">discovery_override</code> in
                    its source tags for later review.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="label">Keyword text <span className="text-danger">*</span></label>
              <input
                type="text"
                value={draft.keyword}
                onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
                placeholder={item.analysis.inferredKeyword ?? "Enter primary search term"}
                className="input"
                dir={draft.language === "he" ? "rtl" : "ltr"}
                autoFocus
              />
              <div className="mt-1.5 flex items-center justify-between gap-2">
                {item.analysis.inferredKeyword ? (
                  <p className="text-2xs text-muted">Pre-filled from analyzer's inferred keyword.</p>
                ) : <span />}
                <button
                  type="button"
                  onClick={() => suggest.mutate()}
                  disabled={suggest.isPending}
                  className="btn-ghost flex shrink-0 items-center gap-1 text-2xs"
                  title="Extract candidate keywords from the title and rank them by real monthly search volume"
                >
                  <Sparkles className="h-3 w-3" />
                  {suggest.isPending ? "Researching…" : "Suggest keyword"}
                </button>
              </div>

              {suggest.data && (
                <div className="mt-2 rounded-lg border border-line bg-canvas p-2">
                  <div className="mb-1 text-2xs uppercase tracking-wider text-muted">
                    Candidates {suggest.data.hasVolumes ? "· monthly searches (IL)" : "· (no volume data)"}
                  </div>
                  <div className="space-y-0.5">
                    {suggest.data.candidates.map((c) => (
                      <button
                        key={c.keyword}
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, keyword: c.keyword }))}
                        dir={draft.language === "he" ? "rtl" : "ltr"}
                        className={`flex w-full items-center justify-between gap-3 rounded px-2 py-1 text-left text-xs transition hover:bg-white ${
                          draft.keyword === c.keyword ? "bg-white ring-1 ring-brand/40" : ""
                        }`}
                      >
                        <span className="min-w-0 truncate">
                          {c.suggested && <span className="mr-1 text-brand-700" title="Suggested">●</span>}
                          {c.keyword}
                        </span>
                        <span className="shrink-0 font-mono text-2xs text-muted">
                          {c.searchVolume == null ? "—" : `${c.searchVolume.toLocaleString()}/mo`}
                        </span>
                      </button>
                    ))}
                  </div>
                  {suggest.data.note && <p className="mt-1 text-2xs text-muted">{suggest.data.note}</p>}
                </div>
              )}
              {suggest.error && (
                <p className="mt-1 text-2xs text-danger">Suggestion failed: {(suggest.error as Error).message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Language</label>
                <select
                  value={draft.language}
                  onChange={(e) => setDraft({ ...draft, language: e.target.value as Language })}
                  className="input"
                >
                  <option value="he">Hebrew</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="label">Cluster</label>
                <select
                  value={draft.clusterId ?? ""}
                  onChange={(e) => setDraft({ ...draft, clusterId: e.target.value || null })}
                  className="input"
                >
                  <option value="">— Unassigned —</option>
                  {(clustersQuery.data?.clusters ?? []).map((c: ClusterView) => (
                    <option key={c.id} value={c.id}>
                      {c.nameEn}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Priority</label>
                <select
                  value={draft.priority ?? "medium"}
                  onChange={(e) => setDraft({ ...draft, priority: e.target.value as Priority })}
                  className="input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="label">Intent</label>
                <select
                  value={draft.intent ?? "informational"}
                  onChange={(e) => setDraft({ ...draft, intent: e.target.value as PromoteDiscoveryInput["intent"] })}
                  className="input"
                >
                  <option value="informational">Informational</option>
                  <option value="commercial">Commercial</option>
                  <option value="transactional">Transactional</option>
                  <option value="navigational">Navigational</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.pillarPage ?? false}
                onChange={(e) => setDraft({ ...draft, pillarPage: e.target.checked })}
              />
              <span>Treat as a pillar page (3,000+ words, hub for the cluster)</span>
            </label>

            {(item.analysis.recommendation || item.analysis.gapAnalysis) && (
              <div className="rounded-lg bg-canvas-deep/40 p-3 ring-1 ring-line">
                <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Carrying forward</div>
                {item.analysis.recommendation && (
                  <div className="mt-1 text-xs">
                    <span className="text-muted">Recommendation:</span> {item.analysis.recommendation}
                  </div>
                )}
                {item.analysis.gapAnalysis && (
                  <div className="mt-1 text-xs">
                    <span className="text-muted">Gap analysis:</span> {item.analysis.gapAnalysis}
                  </div>
                )}
              </div>
            )}
          </div>

          {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700">{error}</div>}

          <footer className="flex items-center justify-end gap-2">
            <button type="button" onClick={onCancel} className="btn-secondary btn-sm" disabled={promote.isPending}>
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm" disabled={promote.isPending}>
              <Sparkles className="h-3.5 w-3.5" />
              {promote.isPending ? "Promoting…" : (override ? "Add anyway" : "Promote")}
            </button>
          </footer>
        </form>
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Utilities
// ───────────────────────────────────────────────────────────────────────────

/**
 * Detect whether a Claude-generated recommendation is telling the operator
 * to skip / pass on this discovery. Both URL + YouTube analyzers are prompted
 * to lead skip recommendations with the word "Skip", so a lenient prefix
 * match is enough. We also catch "out of scope" phrasing as a safety net for
 * future prompt drift.
 */
function isSkipRecommendation(rec: string | null | undefined): boolean {
  if (!rec) return false;
  const lower = rec.toLowerCase().trim();
  return (
    lower.startsWith("skip") ||
    lower.startsWith("out of scope") ||
    lower.includes("outside alternabe")
  );
}

function shortenUrl(u: string): string {
  try {
    const url = new URL(u);
    return url.hostname.replace(/^www\./, "") + url.pathname.slice(0, 50);
  } catch {
    return u.slice(0, 60);
  }
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}
